import React from 'react';
import { useTranslation } from 'react-i18next';

import { useGetUserReportByIdQuery } from '@/store/services/reports';
import { DetailSection } from '../SharedComponents/DetailsSection';
import UserInfo from '../SharedComponents/UserInfo';
import BusinessCardAttachment from '@/components/Home/BusinessCardAttachments';
import { usePublicState } from '@/store/slices/publicSlice';

import ReportDrawer from '../SharedComponents/ReportDrawer';

export interface AdditionInfo {
  country: string;
  state: string;
  street: string;
}

export interface ProfileDocument {
  id: string;
  profileId: string;
  userId: string;
  type: string;
  name: string;
  blobName: string;
  blobFileUrl: string;
  blobSize: number;
}

export interface PapersType {
  taxNumber: string;
  comRegisteration: string;
  applicantType: string;
  comRegisterationDocs: ProfileDocument[];
  nationalIdDocs: ProfileDocument[];
  taxNumberDocs: ProfileDocument[];
  delegationDocs: ProfileDocument[];
}

export interface UserInfo {
  profileId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string | null;
  userName: string;
  companyName: string;
  companyType: string;
}

export interface UserDetailsReport {
  addressInfo: AdditionInfo;
  userInfo: UserInfo;
  papers: PapersType;
}

interface RequestDetailsDrawerProps {
  listOfIds: string[] | [];
}

export const RequestDetailsDrawer: React.FC<RequestDetailsDrawerProps> = ({
  listOfIds,
}) => {
  const { modalId } = usePublicState();
  const { t } = useTranslation();

  const isDrawerOpen = Boolean(modalId && listOfIds?.includes(modalId));

  const { data: reportDetails, isFetching } = useGetUserReportByIdQuery(
    modalId as string,
    { skip: !isDrawerOpen }
  );

  const response = reportDetails?.payload;

  const requestInfoItems = [
    {
      label: t('reports.users.firstName', 'First name'),
      value: response?.userInfo?.firstName || '-',
    },
    {
      label: t('reports.users.lastName', 'Last name'),
      value: response?.userInfo?.lastName || '-',
    },
    {
      label: t('reports.users.mail', 'E-mail'),
      value: response?.userInfo?.email || '-',
    },
    {
      label: t('reports.users.phoneNumber', 'Phone number'),
      value: response?.userInfo?.phone || '-',
    },
    {
      label: t('reports.users.birthDate', 'Date of birth'),
      value: response?.userInfo?.birthDate || '-',
    },
    {
      label: t('reports.users.username', 'Username'),
      value: response?.userInfo?.userName || '-',
    },
    {
      label: t('reports.users.companyName', 'Company name'),
      value: response?.userInfo?.companyName,
    },
    {
      label: t('reports.users.companyType', 'Company type'),
      value: response?.userInfo?.companyType,
    },
  ];

  const additionalInfoItems = [
    {
      label: t('reports.users.country', 'Country'),
      value: response?.addressInfo?.country || '-',
    },
    {
      label: t('reports.users.state', 'State'),
      value: response?.addressInfo?.state || '-',
    },
    {
      label: t('reports.users.street', 'Street address'),
      value: response?.addressInfo?.street || '-',
    },
  ];
  const companyPapers = [
    {
      label: t('reports.users.taxNumber', 'Tax number'),
      value: response?.papers?.taxNumber || '-',
    },
    {
      label: t('reports.users.commercialRegister', 'Commercial register'),
      value: response?.papers?.comRegisteration || '-',
    },
    {
      label: t('reports.users.applicantType', 'Applicant type'),
      value: response?.papers?.applicantType || '-',
    },
  ];

  return (
    <ReportDrawer
      title={t('reports.users.userInformation', 'User Information')}
      listOfIds={listOfIds}
    >
      <div className="space-y-6">
        {/* Request Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.users.userInformation', 'User Information')}
          items={requestInfoItems?.filter((i) => !!i.value)}
        />

        {/* Additional Information Section */}
        <DetailSection
          isLoading={isFetching}
          title={t('reports.users.addressInformation', 'Address information')}
          items={additionalInfoItems}
        />

        {!!response?.papers?.taxNumber && (
          <DetailSection
            isLoading={isFetching}
            title={t('reports.users.companyPapers', 'Company papers')}
            items={companyPapers}
            extraInfo={
              <div className="space-y-2">
                <BusinessCardAttachment
                  title={t(
                    'home.commercialRegisterDocuments',
                    'Commercial register documents'
                  )}
                  attachments={response?.papers?.comRegisterationDocs}
                />
                <BusinessCardAttachment
                  title={t('home.nationalDocuments', 'National ID Documents')}
                  attachments={response?.papers?.nationalIdDocs}
                />
                <BusinessCardAttachment
                  title={t('home.taxNumberDocuments', 'Tax number documents')}
                  attachments={response?.papers?.taxNumberDocs}
                />
                {!!response?.papers?.delegationDocs?.length && (
                  <BusinessCardAttachment
                    title={t('home.delegationDocuments')}
                    attachments={response?.papers?.delegationDocs}
                  />
                )}
              </div>
            }
          />
        )}
      </div>
    </ReportDrawer>
  );
};
